import React, { useState, useEffect } from 'react';
if (typeof document !== 'undefined' && !document.getElementById('leaflet-css')) {
  const link = document.createElement('link');
  link.id = 'leaflet-css'; link.rel = 'stylesheet';
  link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  document.head.appendChild(link);
}
import { Modal, View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator, Pressable } from 'react-native';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
const L = require('leaflet');
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const LeafletMap    = MapContainer as any;
const LeafletTile   = TileLayer as any;
const LeafletMarker = Marker as any;
const LeafletPopup  = Popup as any;

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const DP_STATUS_COLOR: Record<string, string> = {
  ACCEPTED: '#3b82f6',
  PICKED: '#8b5cf6',
  HEADING: '#f59e0b',
  DELIVERED: '#10b981',
  CANCELLED: '#ef4444',
  CANCEL: '#ef4444',
};

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 15); return; }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, JSON.stringify(points)]);
  return null;
}

const pickupIcon = L.divIcon({
  html: `<div style="background:#32a852;width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`,
  iconSize: [34, 34], iconAnchor: [17, 34], className: '',
});

const dropIcon = L.divIcon({
  html: `<div style="background:#3b82f6;width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`,
  iconSize: [34, 34], iconAnchor: [17, 34], className: '',
});

const dpIcon = L.divIcon({
  html: `<div style="background:#f59e0b;width:32px;height:32px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><span class="material-icons-round" style="font-size:17px;color:#fff;line-height:1">delivery_dining</span></div>`,
  iconSize: [32, 32], iconAnchor: [16, 16], className: '',
});

const currentRiderIcon = L.divIcon({
  html: `<div style="background:#16a34a;width:36px;height:36px;border-radius:50%;border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center"><span class="material-icons-round" style="font-size:18px;color:#fff;line-height:1">delivery_dining</span></div>`,
  iconSize: [36, 36], iconAnchor: [18, 18], className: '',
});

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371, dLat = ((lat2 - lat1) * Math.PI) / 180, dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
const fmtDist = (km: number) => km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;

interface Rider {
  docId: string; partnerId?: string; name?: string; phone?: string; active?: boolean;
  available?: boolean; currentOrderId?: string; accepted?: boolean;
  currentLocation?: { latitude?: number; longitude?: number };
}
interface OrderLite {
  id: string; orderNumber?: string[];
  deliveryPartnerId?: string;
  deliveryPartnerStatus?: string;
  sellerLocation?: { name?: string; address?: string; latitude?: number; longitude?: number };
  userLocation?: { name?: string; address?: string; latitude?: number; longitude?: number };
}
interface Props { visible: boolean; order: OrderLite | null; onClose: () => void; }

const RestaurantOrderMapModal: React.FC<Props> = ({ visible, order, onClose }) => {
  const [partners, setPartners] = useState<Rider[]>([]);
  const [loading, setLoading]   = useState(true);

  const rLat = order?.sellerLocation?.latitude;
  const rLng = order?.sellerLocation?.longitude;
  const dLat = order?.userLocation?.latitude;
  const dLng = order?.userLocation?.longitude;
  const hasMap = !!(rLat && rLng);
  const deliveryDist = rLat && rLng && dLat && dLng ? haversineKm(rLat, rLng, dLat, dLng) : null;

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'zapee_delivery_partners'), (snap) => {
      setPartners(snap.docs.map(d => ({ docId: d.id, ...(d.data() as any) })) as Rider[]);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [visible]);

  // Only connected (active/online) riders
  const connectedPartners = partners
    .filter(r => r.active)
    .map(r => {
      const lat = r.currentLocation?.latitude, lon = r.currentLocation?.longitude;
      const dist = lat != null && lon != null && rLat && rLng ? haversineKm(lat, lon, rLat, rLng) : null;
      return { rider: r, dist };
    })
    .sort((a, b) => {
      if (a.dist === null) return 1;
      if (b.dist === null) return -1;
      return a.dist - b.dist;
    });

  // Points for fitBounds: pickup + drop + current rider
  const currentRider = connectedPartners.find(({ rider }) =>
    order?.deliveryPartnerId && (rider.partnerId === order.deliveryPartnerId || rider.docId === order.deliveryPartnerId)
  )?.rider;
  const fitPoints: [number, number][] = [];
  if (rLat && rLng) fitPoints.push([rLat, rLng]);
  if (dLat && dLng) fitPoints.push([dLat, dLng]);
  const crLat = currentRider?.currentLocation?.latitude, crLng = currentRider?.currentLocation?.longitude;
  if (crLat && crLng) fitPoints.push([crLat, crLng]);

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <Pressable style={s.overlay} onPress={onClose}>
        <Pressable style={s.sheet} onPress={() => {}}>

          {/* Handle bar */}
          <View style={s.handle} />

          {/* Header */}
          <View style={s.header}>
            <View style={{ flex: 1 }}>
              <Text style={s.title}>Pickup Info</Text>
              {order?.orderNumber?.[0] ? <Text style={s.subtitle}>#{order.orderNumber[0]}</Text> : null}
            </View>
            <TouchableOpacity style={s.closeBtn} onPress={onClose}>
              <span className="material-icons-round" style={{ fontSize: 20, color: '#6B7280', lineHeight: 1 }}>close</span>
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>

            {/* Map */}
            <View style={s.mapWrap}>
              {hasMap ? (
                <LeafletMap center={[rLat!, rLng!]} zoom={14} style={{ width: '100%', height: '100%' }} zoomControl={false}>
                  <LeafletTile url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                  <FitBounds points={fitPoints} />
                  {/* Pickup marker */}
                  <LeafletMarker position={[rLat!, rLng!]} icon={pickupIcon}>
                    <LeafletPopup><strong>Pickup</strong><br />{order?.sellerLocation?.name}</LeafletPopup>
                  </LeafletMarker>
                  {/* Drop marker */}
                  {dLat && dLng && (
                    <LeafletMarker position={[dLat, dLng]} icon={dropIcon}>
                      <LeafletPopup><strong>Drop</strong><br />{order?.userLocation?.name}</LeafletPopup>
                    </LeafletMarker>
                  )}
                  {/* Current assigned rider marker only */}
                  {crLat && crLng && (
                    <LeafletMarker position={[crLat, crLng]} icon={currentRiderIcon}>
                      <LeafletPopup><strong>{currentRider?.name ?? 'Rider'}</strong> · Current<br />{currentRider?.phone}</LeafletPopup>
                    </LeafletMarker>
                  )}
                </LeafletMap>
              ) : (
                <View style={s.noMap}><Text style={s.noMapTxt}>No location data</Text></View>
              )}
            </View>

            {/* Pickup + Drop card */}
            <View style={s.pickupCard}>
              {/* Pickup row */}
              <View style={s.routeRow}>
                <View style={s.routeDotGreen} />
                <View style={{ flex: 1 }}>
                  <Text style={s.pickupLabel}>Pick From</Text>
                  <Text style={s.pickupName} numberOfLines={1}>{order?.sellerLocation?.name ?? '—'}</Text>
                  {order?.sellerLocation?.address
                    ? <Text style={s.pickupAddress} numberOfLines={1}>{order.sellerLocation.address}</Text>
                    : null}
                </View>
              </View>
              {/* Connector line */}
              <View style={s.routeLine} />
              {/* Drop row */}
              <View style={s.routeRow}>
                <View style={s.routeDotBlue} />
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={[s.pickupLabel, { color: '#2563eb' }]}>Deliver To</Text>
                    {deliveryDist !== null && (
                      <View style={s.distChip}>
                        <Text style={s.distChipTxt}>{fmtDist(deliveryDist)}</Text>
                      </View>
                    )}
                  </View>
                  <Text style={s.pickupName} numberOfLines={1}>{order?.userLocation?.name ?? '—'}</Text>
                  {order?.userLocation?.address
                    ? <Text style={s.pickupAddress} numberOfLines={1}>{order.userLocation.address}</Text>
                    : null}
                </View>
              </View>
            </View>

            {/* Assigned rider */}
            <View style={s.section}>
              <View style={s.sectionHeader}>
                <span className="material-icons-round" style={{ fontSize: 14, color: '#16a34a', lineHeight: 1, marginRight: 5 }}>delivery_dining</span>
                <Text style={s.sectionTitle}>Assigned Rider</Text>
              </View>

              {loading ? (
                <ActivityIndicator color="#16a34a" style={{ marginVertical: 24 }} />
              ) : !currentRider ? (
                <View style={s.empty}><Text style={s.emptyTxt}>No rider assigned</Text></View>
              ) : (() => {
                  const dist = crLat && crLng && rLat && rLng ? haversineKm(crLat, crLng, rLat, rLng) : null;
                  const inTrip = !!currentRider.currentOrderId;
                  return (
                    <View style={[s.riderRow, s.riderRowCurrent]}>
                      <View style={s.riderAvatar}>
                        <Text style={s.riderAvatarTxt}>{(currentRider.name ?? 'D').charAt(0).toUpperCase()}</Text>
                        <View style={[s.onlineDot, { backgroundColor: '#22c55e' }]} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <Text style={s.riderName}>{currentRider.name ?? 'Partner'}</Text>
                          <View style={s.assignedBadge}><Text style={s.assignedTxt}>Assigned</Text></View>
                        </View>
                        <Text style={s.riderPhone}>{currentRider.phone ?? '—'}</Text>
                        {order?.deliveryPartnerStatus && (() => {
                          const key = order.deliveryPartnerStatus!.toUpperCase();
                          const color = DP_STATUS_COLOR[key] ?? '#6B7280';
                          return (
                            <View style={[s.dpStatusBadge, { backgroundColor: color + '18', borderColor: color + '50' }]}>
                              <View style={[s.dpStatusDot, { backgroundColor: color }]} />
                              <Text style={[s.dpStatusTxt, { color }]}>{order.deliveryPartnerStatus}</Text>
                            </View>
                          );
                        })()}
                      </View>
                      <View style={{ alignItems: 'flex-end', gap: 4 }}>
                        {dist !== null && (
                          <View style={[s.distBadge, { backgroundColor: dist < 3 ? '#dcfce7' : dist < 8 ? '#fef9c3' : '#fee2e2' }]}>
                            <Text style={[s.distTxt, { color: dist < 3 ? '#16a34a' : dist < 8 ? '#ca8a04' : '#dc2626' }]}>{fmtDist(dist)}</Text>
                          </View>
                        )}
                        <View style={[s.statusBadge, inTrip ? s.badgeTrip : s.badgeFree]}>
                          <Text style={[s.statusTxt, { color: inTrip ? '#d97706' : '#3b82f6' }]}>{inTrip ? 'In Trip' : 'Free'}</Text>
                        </View>
                      </View>
                    </View>
                  );
                })()
              }
            </View>

            <View style={{ height: 24 }} />
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default RestaurantOrderMapModal;

const s = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: 16 },
  sheet: { backgroundColor: '#fff', borderRadius: 20, width: '100%', maxWidth: 400, maxHeight: '90%', overflow: 'hidden', shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.2, shadowRadius: 24, elevation: 16 },
  handle: { width: 36, height: 4, backgroundColor: '#E2E8F0', borderRadius: 2, alignSelf: 'center', marginTop: 10, marginBottom: 4 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingTop: 8, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  title: { fontSize: 16, fontWeight: '700', color: '#1e293b' },
  subtitle: { fontSize: 12, color: '#64748B', marginTop: 1 },
  closeBtn: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#F1F5F9', alignItems: 'center', justifyContent: 'center' },

  mapWrap: { height: 220, backgroundColor: '#f1f5f9' },
  noMap: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  noMapTxt: { color: '#9CA3AF', fontSize: 13 },

  pickupCard: { margin: 14, padding: 14, backgroundColor: '#f8fafc', borderRadius: 14, borderWidth: 1.5, borderColor: '#e2e8f0' },
  routeRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  routeDotGreen: { width: 12, height: 12, borderRadius: 6, backgroundColor: '#22c55e', marginTop: 3, flexShrink: 0 },
  routeDotBlue: { width: 12, height: 12, borderRadius: 6, backgroundColor: '#3b82f6', marginTop: 3, flexShrink: 0 },
  routeLine: { width: 2, height: 16, backgroundColor: '#cbd5e1', marginLeft: 5, marginVertical: 2 },
  pickupLabel: { fontSize: 10, fontWeight: '700', color: '#16a34a', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 1 },
  pickupName: { fontSize: 13, fontWeight: '700', color: '#1e293b' },
  pickupAddress: { fontSize: 11, color: '#64748B', marginTop: 1 },
  assignedBadge: { backgroundColor: '#dcfce7', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2 },
  assignedTxt: { fontSize: 10, fontWeight: '700', color: '#16a34a' },
  dpStatusBadge: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', marginTop: 4, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 8, borderWidth: 1, gap: 4 },
  dpStatusDot: { width: 6, height: 6, borderRadius: 3 },
  dpStatusTxt: { fontSize: 11, fontWeight: '700' },
  distChip: { backgroundColor: '#eff6ff', borderRadius: 8, paddingHorizontal: 7, paddingVertical: 2 },
  distChipTxt: { fontSize: 10, fontWeight: '700', color: '#2563eb' },

  section: { paddingHorizontal: 14 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  sectionTitle: { fontSize: 13, fontWeight: '700', color: '#374151' },
  countBadge: { marginLeft: 6, backgroundColor: '#FEF3C7', borderRadius: 10, paddingHorizontal: 7, paddingVertical: 2 },
  countTxt: { fontSize: 11, fontWeight: '700', color: '#d97706' },

  empty: { alignItems: 'center', paddingVertical: 24 },
  emptyTxt: { color: '#9CA3AF', fontSize: 13 },

  riderRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 8, borderBottomWidth: 1, borderBottomColor: '#F1F5F9', borderRadius: 10, marginBottom: 2 },
  riderRowCurrent: { backgroundColor: '#f0fdf4', borderWidth: 1.5, borderColor: '#86efac', borderBottomColor: '#86efac' },
  riderAvatar: { position: 'relative', width: 40, height: 40, borderRadius: 20, backgroundColor: '#FEF3C7', alignItems: 'center', justifyContent: 'center' },
  riderAvatarTxt: { fontSize: 16, fontWeight: '700', color: '#d97706' },
  onlineDot: { position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: 5, borderWidth: 1.5, borderColor: '#fff' },
  riderName: { fontSize: 13, fontWeight: '700', color: '#1e293b' },
  riderPhone: { fontSize: 11, color: '#64748B', marginTop: 1 },
  distBadge: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 10 },
  distTxt: { fontSize: 11, fontWeight: '700' },
  statusBadge: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 6 },
  badgeFree: { backgroundColor: '#eff6ff' },
  badgeTrip: { backgroundColor: '#fef3c7' },
  statusTxt: { fontSize: 10, fontWeight: '600' },
});
